import {
  addMinutes, parse, format, isBefore, isAfter, isEqual,
  startOfMonth, endOfMonth, eachDayOfInterval,
} from 'date-fns';
import { doc, getDoc, getDocs, collection, query, where, documentId } from 'firebase/firestore';
import { db, LISTING_PARENTS } from './firebase';
import type { TimeSlot } from '../types';

/** Subcollection id is case-sensitive; some projects use `Availability` in the console. */
const AVAILABILITY_SUBCOLLECTIONS = ['availability', 'Availability'] as const;

interface BusinessHours {
  open?: string;
  close?: string;
  closed?: boolean;
}

interface AvailabilityParams {
  date: Date;
  serviceDuration: number;
  listingId: string;
  businessHours: Record<string, BusinessHours>; // Changed from weekSchedule
  bufferMinutes?: number; // Simplified buffer
  minNoticeHours?: number; // Changed from minutes
}

/**
 * Calculate available time slots for a given date.
 * Reads `availability/{YYYY-MM-DD}`: `slots` are **free** intervals `{ start_time, end_time }` (HH:mm),
 * already net of blocks. Offered times are a 15-minute grid within business hours that fit inside a free window.
 */
export async function calculateAvailableSlots(params: AvailabilityParams): Promise<TimeSlot[]> {
  const {
    date,
    serviceDuration,
    listingId,
    businessHours,
    bufferMinutes = 0,
    minNoticeHours = 0,
  } = params;

  const slots: TimeSlot[] = [];

  // Get day of week
  const dayOfWeek = format(date, 'EEEE').toLowerCase();

  // Get business hours for this day
  const daySchedule = businessHours[dayOfWeek];

  // Check if closed or no hours set
  if (!daySchedule || daySchedule.closed || !daySchedule.open || !daySchedule.close) {
    return slots; // Closed on this day
  }

  const businessStart = daySchedule.open;
  const businessEnd = daySchedule.close;

  // Parse business hours
  const startTime = parse(businessStart, 'HH:mm', date);
  const endTime = parse(businessEnd, 'HH:mm', date);

  // Calculate minimum booking time (current time + min notice)
  const now = new Date();
  const minBookingTime = addMinutes(now, minNoticeHours * 60);

  const dateString = format(date, 'yyyy-MM-dd');
  const freeWindows = await getFreeWindowsForDate(listingId, dateString);

  // Generate 15-minute intervals
  let currentSlot = startTime;

  while (isBefore(currentSlot, endTime)) {
    const slotEndTime = addMinutes(currentSlot, serviceDuration);

    // Check if slot end time is within business hours
    if (isAfter(slotEndTime, endTime)) {
      break;
    }

    // Check if slot meets minimum notice requirement
    const slotDateTime = new Date(date);
    slotDateTime.setHours(currentSlot.getHours(), currentSlot.getMinutes(), 0, 0);

    const meetsMinNotice = isAfter(slotDateTime, minBookingTime);

    const inFreeWindow = fitsInFreeWindows(
      currentSlot,
      slotEndTime,
      freeWindows,
      bufferMinutes
    );

    slots.push({
      time: format(currentSlot, 'HH:mm'),
      available: meetsMinNotice && inFreeWindow,
    });

    // Move to next 15-minute slot
    currentSlot = addMinutes(currentSlot, 15);
  }

  return slots;
}

type Interval = { start_time: string; end_time: string };

/**
 * Free bookable intervals from Firestore `availability/{date}`.
 */
async function getFreeWindowsForDate(listingId: string, date: string): Promise<Interval[]> {
  for (const parent of LISTING_PARENTS) {
    for (const sub of AVAILABILITY_SUBCOLLECTIONS) {
      try {
        const snap = await getDoc(doc(db, parent, listingId, sub, date));
        if (!snap.exists()) continue;
        return (snap.data().slots as Interval[]) || [];
      } catch (error) {
        console.error(`Error fetching free availability (${parent}/${sub}):`, error);
      }
    }
  }
  return [];
}

/**
 * True if [slotStart, slotEnd] plus buffer margins lies entirely inside at least one free window.
 */
function fitsInFreeWindows(
  slotStart: Date,
  slotEnd: Date,
  freeWindows: Interval[],
  bufferMinutes: number
): boolean {
  if (freeWindows.length === 0) return false;

  const paddedStart = addMinutes(slotStart, -bufferMinutes);
  const paddedEnd = addMinutes(slotEnd, bufferMinutes);

  return freeWindows.some(w => {
    const wStart = parse(w.start_time.substring(0, 5), 'HH:mm', slotStart);
    const wEnd = parse(w.end_time.substring(0, 5), 'HH:mm', slotStart);
    return (
      (isAfter(paddedStart, wStart) || isEqual(paddedStart, wStart)) &&
      (isBefore(paddedEnd, wEnd) || isEqual(paddedEnd, wEnd))
    );
  });
}

/**
 * Format time for display (e.g., "09:00" -> "9:00 AM")
 */
export function formatTimeForDisplay(time: string): string {
  const parsed = parse(time, 'HH:mm', new Date());
  return format(parsed, 'h:mm a');
}

/**
 * Calculate ISO week string for a date
 */
export function getISOWeekString(date: Date): string {
  const year = date.getFullYear();
  const week = getISOWeek(date);
  return `${year}-W${week.toString().padStart(2, '0')}`;
}

/**
 * Get ISO week number for a date
 */
function getISOWeek(date: Date): number {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  }
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
}

/**
 * Check if a date is in the bookable window
 */
export function isDateBookable(
  date: Date,
  bookingWindowDays: number = 60,
  minNoticeDays: number = 0
): boolean {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  const minDate = new Date(now);
  minDate.setDate(minDate.getDate() + minNoticeDays);

  const maxDate = new Date(now);
  maxDate.setDate(maxDate.getDate() + bookingWindowDays);

  return !isBefore(targetDate, minDate) && !isAfter(targetDate, maxDate);
}

/**
 * Determine which dates in a month have zero available slots.
 * Returns a Set of date strings (via Date.toDateString()) for O(1) lookup.
 *
 * Optimised: closed days and past/out-of-window days are resolved locally.
 * Only "open" future days need a firebase query (batched per month).
 */
export async function getUnavailableDatesForMonth(params: {
  month: Date;
  serviceDuration: number;
  listingId: string;
  businessHours: Record<string, BusinessHours>;
  bufferMinutes?: number;
  minNoticeHours?: number;
  bookingWindowDays?: number;
}): Promise<Set<string>> {
  const {
    month,
    serviceDuration,
    listingId,
    businessHours,
    bufferMinutes = 0,
    minNoticeHours = 0,
    bookingWindowDays = 60,
  } = params;

  const unavailable = new Set<string>();

  const days = eachDayOfInterval({
    start: startOfMonth(month),
    end: endOfMonth(month),
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Separate days into "needs Firebase check" vs "locally unavailable"
  const daysNeedingCheck: Date[] = [];

  for (const day of days) {
    const dayNorm = new Date(day);
    dayNorm.setHours(0, 0, 0, 0);

    // Past dates
    if (isBefore(dayNorm, today)) {
      unavailable.add(day.toDateString());
      continue;
    }

    // Outside booking window
    if (!isDateBookable(day, bookingWindowDays)) {
      unavailable.add(day.toDateString());
      continue;
    }

    // Closed day (from business hours)
    const dayOfWeek = format(day, 'EEEE').toLowerCase();
    const schedule = businessHours[dayOfWeek];
    if (!schedule || schedule.closed || !schedule.open || !schedule.close) {
      unavailable.add(day.toDateString());
      continue;
    }

    daysNeedingCheck.push(day);
  }

  if (daysNeedingCheck.length === 0) return unavailable;

  // Batch-fetch free windows for the month (first subcollection path that returns any day docs wins)
  const monthStart = format(daysNeedingCheck[0], 'yyyy-MM-dd');
  const monthEnd = format(daysNeedingCheck[daysNeedingCheck.length - 1], 'yyyy-MM-dd');

  const freeByDate: Record<string, Interval[]> = {};
  outer: for (const parent of LISTING_PARENTS) {
    for (const sub of AVAILABILITY_SUBCOLLECTIONS) {
      try {
        const snap = await getDocs(
          query(
            collection(db, parent, listingId, sub),
            where(documentId(), '>=', monthStart),
            where(documentId(), '<=', monthEnd)
          )
        );
        if (snap.empty) continue;
        for (const d of snap.docs) {
          freeByDate[d.id] = (d.data().slots as Interval[]) || [];
        }
        break outer;
      } catch (err) {
        console.error(`Error batch-fetching availability (${parent}/${sub}):`, err);
      }
    }
  }

  const datesWithAvailabilityDoc = new Set(Object.keys(freeByDate));

  // Check each open day for at least one available slot
  const now = new Date();
  const minBookingTime = addMinutes(now, minNoticeHours * 60);

  for (const day of daysNeedingCheck) {
    const dayOfWeek = format(day, 'EEEE').toLowerCase();
    const schedule = businessHours[dayOfWeek]!;
    const startTime = parse(schedule.open!, 'HH:mm', day);
    const endTime = parse(schedule.close!, 'HH:mm', day);
    const dateString = format(day, 'yyyy-MM-dd');

    if (!datesWithAvailabilityDoc.has(dateString)) {
      unavailable.add(day.toDateString());
      continue;
    }

    const freeWindows = freeByDate[dateString] || [];

    let hasAvailable = false;
    let currentSlot = startTime;

    while (isBefore(currentSlot, endTime)) {
      const slotEndTime = addMinutes(currentSlot, serviceDuration);
      if (isAfter(slotEndTime, endTime)) break;

      const slotDateTime = new Date(day);
      slotDateTime.setHours(currentSlot.getHours(), currentSlot.getMinutes(), 0, 0);
      const meetsMinNotice = isAfter(slotDateTime, minBookingTime);

      if (meetsMinNotice) {
        if (fitsInFreeWindows(currentSlot, slotEndTime, freeWindows, bufferMinutes)) {
          hasAvailable = true;
          break; // One available slot is enough — this day is bookable
        }
      }

      currentSlot = addMinutes(currentSlot, 15);
    }

    if (!hasAvailable) {
      unavailable.add(day.toDateString());
    }
  }

  return unavailable;
}
